import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';

const values = [
  {
    icon: 'ri-stack-line',
    title: '内容整理',
    desc: '按最新、热门、经典三条线索重新组织影片资料，减少在海量列表里反复翻页的时间。',
  },
  {
    icon: 'ri-eye-line',
    title: '浏览体验',
    desc: '深色界面降低长时间浏览的视觉疲劳，卡片信息保持统一，详情页一次看全资料。',
  },
  {
    icon: 'ri-lock-line',
    title: '隐私克制',
    desc: '不要求注册，不收集观看记录。收藏只写入当前浏览器的本地存储，随时可以清除。',
  },
];

const disclaimers = [
  '本站为影视门户类产品的前端界面演示，所有影片名称、简介、评分、播放量、演职人员均为虚构示例，不对应任何真实作品或真实人物。',
  '站内不存储、不托管、不分发任何视频文件，播放区域仅模拟播放状态与控制条交互，不会加载真实片源。',
  '本站不提供下载、不提供播放源地址、不接受内容投稿，也不引导用户前往任何第三方片源站点。',
  '站内展示的收藏、搜索、筛选与分页功能均为前端逻辑演示，数据不会上传服务器，清除浏览器数据后即失效。',
  '本站仅面向十八岁以上用户。若你未满十八岁，请立即离开本站。',
];

export default function AboutPage() {
  usePageMeta({
    title: '关于我们 - av免费网站',
    description:
      '了解 av免费网站 的定位、内容整理方式与免责声明。本站为影视门户界面演示站，影片资料均为虚构示例，不提供真实片源。',
    keywords: '关于av免费网站,影视门户,免责声明,高清AV,AV在线观看',
  });

  return (
    <div>
      <section className="border-b border-background-200/70 bg-background-100">
        <div className="max-w-page mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-10 md:pb-14">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
            <span className="text-foreground-800">关于我们</span>
          </nav>

          <div className="mt-7 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-8 items-end">
            <div>
              <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">About the Project</p>
              <h1 className="mt-3 text-3xl md:text-5xl font-black tracking-tight leading-tight">
                关于 av免费网站
              </h1>
              <p className="mt-4 max-w-2xl text-sm md:text-base leading-relaxed text-foreground-700">
                一个以「精选、高清、免费浏览」为出发点的影视门户演示项目。我们把注意力放在三件事上：内容怎么被找到、信息怎么被读清楚、以及浏览过程中不该被打扰。
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: '24', label: '示例影片' },
                { value: '3', label: '内容分类' },
                { value: '18+', label: '访问限制' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-300/60 bg-background-50 px-3 py-4 text-center">
                  <p className="font-label text-2xl md:text-3xl text-primary-400">{stat.value}</p>
                  <p className="mt-1 text-[11px] text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-page mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-8 md:gap-10 items-start">
          <div>
            <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">Our Positioning</p>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">网站定位</h2>
            <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-700">
              <p>
                av免费网站 面向希望在有限时间里快速找到片子的成年用户。首页提供焦点推荐与编辑精选片单，分类页用地区、年份与排序组合缩小范围，详情页一次性给出年份、地区、时长、评分与简介，避免反复跳转确认信息。
              </p>
              <p>
                我们不追求无限滚动的信息流，也不做弹窗推送。每个页面的目标都很具体：帮你在三步之内进入一部影片的详情页，并且清楚地知道自己在看什么。
              </p>
            </div>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {values.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-300/60 bg-background-100 p-4">
                  <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-300 flex items-center justify-center">
                    <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center`} />
                  </span>
                  <h3 className="mt-3 text-sm font-medium text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative w-full rounded-lg overflow-hidden border border-background-300/60">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20photo%20of%20a%20minimal%20editing%20studio%20at%20night%2C%20warm%20amber%20desk%20lamp%20on%20a%20dark%20wooden%20desk%2C%20film%20cans%20and%20a%20notebook%2C%20empty%20monitor%20glow%2C%20deep%20shadows%2C%20premium%20editorial%20interior%20photography%2C%20no%20people%2C%20no%20text&width=800&height=800&seq=av-about-studio&orientation=squarish"
              alt="深色后期工作室，暖光台灯与胶片盒"
              title="av免费网站 - 内容整理工作室"
              className="w-full aspect-square object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/10 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5">
              <p className="text-sm font-medium text-foreground-950">内容整理，从一张干净的页面开始</p>
              <p className="mt-1 text-xs text-foreground-600">配图为站内视觉示意素材</p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200/70 bg-background-100">
        <div className="max-w-page mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="flex flex-col lg:flex-row lg:items-start gap-8">
            <div className="lg:w-[320px] shrink-0">
              <p className="text-[11px] tracking-[0.3em] text-accent-300 font-label uppercase">Disclaimer</p>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">免责声明</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                以下条款适用于本站全部页面与功能演示内容，请在使用前阅读。
              </p>
              <span className="mt-5 inline-flex items-center h-8 px-3 rounded-md border border-accent-500/40 text-accent-300 text-xs tracking-widest font-label">
                18+ ONLY
              </span>
            </div>

            <ol className="flex-1 space-y-3">
              {disclaimers.map((text, index) => (
                <li
                  key={text}
                  className="flex gap-3 rounded-lg border border-background-300/60 bg-background-50 p-4"
                >
                  <span className="w-6 h-6 shrink-0 rounded-md bg-accent-500/15 border border-accent-500/25 text-accent-300 text-xs font-label flex items-center justify-center">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <p className="text-sm leading-relaxed text-foreground-700">{text}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="max-w-page mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-lg border border-background-300/60 bg-background-100 p-5 md:p-6">
            <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">Contact</p>
            <h2 className="mt-2 text-xl font-bold tracking-tight">联系方式</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              如果你对界面、内容分类方式或无障碍体验有建议，可以写信告诉我们。这个邮箱仅用于产品演示，不会接收真实业务请求。
            </p>
            <div className="mt-5 flex items-center gap-3 rounded-md border border-background-300/60 bg-background-50 px-4 py-3">
              <i className="ri-mail-line w-5 h-5 flex items-center justify-center text-primary-400" />
              <a
                href="mailto:feedback@example.com"
                className="text-sm text-foreground-900 hover:text-primary-400 transition-colors cursor-pointer break-all"
              >
                feedback@example.com
              </a>
            </div>
            <p className="mt-3 text-[11px] text-foreground-500">
              演示邮箱，请勿发送真实个人信息。我们通常在两个工作日内回复产品类建议。
            </p>
          </div>

          <div className="rounded-lg border border-background-300/60 bg-background-100 p-5 md:p-6">
            <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">Quick Links</p>
            <h2 className="mt-2 text-xl font-bold tracking-tight">继续浏览</h2>
            <div className="mt-4 space-y-2">
              {[
                { label: '最新上架影片', to: '/category/latest' },
                { label: '本周热门榜单', to: '/category/hot' },
                { label: '经典修复特辑', to: '/category/classic' },
                { label: '常见问题解答', to: '/faq' },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center justify-between rounded-md border border-background-300/60 bg-background-50 px-4 py-3 text-sm text-foreground-800 hover:border-primary-500/60 hover:text-primary-400 transition-colors cursor-pointer"
                >
                  {link.label}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}